import styled from '@emotion/styled';
import { useMemo, useState, useEffect, useRef } from 'react';
import { Card, message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn } from 'ahooks';

import { orderAndPay, pay, OnOrder } from 'services/api/pay';
import { org } from 'services/api/admin';

import useModal from 'hooks/useModal';
import { useSetOrgValue } from 'hooks/business/system/useOrg';
import { useAuthorization } from 'components/Authorization';
import wait from 'utils/wait';

import ServiceOverview from './components/ServiceOverview';
import WelfareCenter from './components/WelfareCenter';
import PromoteBanner from './components/PromoteBanner';
import CompanyInfo from './components/CompanyInfo';
import OrderList, { OrderTableRef } from './components/OrderList';
import Package from './components/Package';
import PayModal, { OrderInfo } from './components/PayModal';

import { usePackageV2 } from './hooks/usePackage';
import useIsValidPackage from './hooks/useIsValidPackage';
import { useSetIncentive } from 'views/Management/NewuserIncentive/state';
import { incentiveInfo } from 'views/Management/NewuserIncentive/service';
import { isQuickChat } from 'productLine';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { refreshComboInfo } from './hooks/useComboInfo';
import { refreshBotUsageSummary } from 'hooks/business/system/useBot';
import { useFlagrWelfareCenterValue } from 'hooks/business/system/useFlagr';
import { useQueryClient } from '@tanstack/react-query';
import StripeSubscriptionModal from './components/StripeSubscriptionModal';
import { handleOrder } from './handleOrder';

enum TabKey {
  OrderList = 'OrderList',
  ServiceInfo = 'ServiceInfo',
  OrderPackage = 'OrderPackage',
}

const Market = () => {
  const { t } = useTranslate();

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { accid = '', orgId } = payload || {};

  const language = useSystemLanguageValue();

  const setOrgValue = useSetOrgValue();
  // 是否有生效套餐
  const isValidPackage = useIsValidPackage();

  const setIncentive = useSetIncentive();

  const [activeTabKey, setActiveTabKey] = useState<TabKey>(
    isValidPackage ? TabKey.ServiceInfo : TabKey.OrderPackage,
  );
  const [packageInfo, setPackageInfo] = useState<Pay.PackageInfoV2>();

  // 套餐信息
  const { fetch: fetchPackageList } = usePackageV2(orgId, 'ORDER');
  // 支付窗口
  const { visible, show, hide, update: updatePayModal, meta } = useModal<OrderInfo>();

  const { marketWelfareBar = true } = useFlagrWelfareCenterValue() || {};

  const refsCurrent = {
    setOrgValue,
    fetchPackageList,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const tabList = useMemo(() => {
    const list = [
      {
        key: TabKey.OrderList,
        tab: t('scene.managementCenter.sellerPlan.title.billingHistory'),
      },
    ];
    if (isValidPackage) {
      list.unshift({
        key: TabKey.ServiceInfo,
        tab: t('market.服务概况'),
      });
    } else {
      list.unshift({
        key: TabKey.OrderPackage,
        tab: t('scene.managementCenter.sellerPlan.title.subscription'),
      });
    }
    return list;
  }, [t, isValidPackage]);

  const onOrder: OnOrder = useMemoizedFn((info) => {
    return handleOrder(info, {
      orderAndPay,
      showPayModal: show,
      onError: () => message.error(t('market.创建订单支付失败，请稍后再试')),
    });
  });

  const onPay = useMemoizedFn((order: Pay.Order) => {
    const {
      orderCode,
      orderDescribe: { accountNum, durationNum, validStartStr, validEndStr },
      amount,
      orderAction,
      packageId,
      trafficPackageType,
      handleNum,
      orderNumber,
    } = order;

    return pay({
      idempotent: `${Date.now()}`,
      orderCode,
      language,
    })
      .then((res) => {
        show({
          accid,
          accountNum,
          durationNum,
          orderCode,
          discountTotalPrice: `${amount}`,
          orderAction,
          ...res,
          payType: res.payType || 'alipay',
          packageId,
          trafficPackageType,
          validStartStr,
          validEndStr,
          handleNum,
          orderNumber,
        });
      })
      .catch(() => {
        message.error(t('market.获取支付信息失败，请稍后再试'));
      });
  });

  const orderListRef = useRef<OrderTableRef>(null);

  const queryClient = useQueryClient();

  // 支付/订阅成功后刷新服务数据
  const refreshAfterPay = useMemoizedFn(() => {
    wait(1000).then(() => {
      org()
        .then(setOrgValue)
        .catch(() => {});

      incentiveInfo()
        .then(setIncentive)
        .catch(() => {});

      orderListRef.current?.fetchOrderList();

      refreshComboInfo();
      refreshBotUsageSummary();
      queryClient.invalidateQueries(['promteConfig']);
      // query-key-factory 生成的实际 key 带 'accounts' 前缀，数组形式匹配不到
      queryClient.invalidateQueries({ queryKey: ['accounts', 'orderedPackage'] });
      queryClient.invalidateQueries({ queryKey: ['accounts', 'lightWeightPackageInfo'] });
    });
  });

  const tabContents = useMemo(() => {
    return {
      // 订单列表
      [TabKey.OrderList]: <OrderList onPay={onPay} ref={orderListRef} />,
      // 服务概况
      [TabKey.ServiceInfo]: <ServiceOverview onOrder={onOrder} />,
      // 订购
      [TabKey.OrderPackage]: packageInfo && (
        <Package packageInfo={packageInfo} onOrder={onOrder} onSubscribed={refreshAfterPay} />
      ),
    };
  }, [packageInfo, onOrder, onPay, refreshAfterPay]);

  useEffect(() => {
    const {
      current: { fetchPackageList },
    } = refs;
    if (!isValidPackage) {
      fetchPackageList()
        .then((rst) => {
          setPackageInfo(rst);
        })
        .catch((e) => {
          message.error(`/get_package_info ORDER ${e?.message}`);
        });
    }

    setActiveTabKey(isValidPackage ? TabKey.ServiceInfo : TabKey.OrderPackage);
  }, [isValidPackage]);

  const onRefresh = (meta: OrderInfo) => {
    const { orderCode, ...others } = meta;

    return pay({
      idempotent: `${Date.now()}`,
      orderCode,
      language,
    })
      .then((res) => {
        updatePayModal(visible, {
          orderCode,
          ...others,
          ...res,
        });
      })
      .catch(() => {
        message.error(t('market.重新加载失败，请稍后再试'));
      });
  };

  return (
    <Container>
      {!isQuickChat && marketWelfareBar && <WelfareCenter />}
      <PromoteBanner />
      <CompanyInfo />
      <Card
        tabList={tabList}
        activeTabKey={activeTabKey}
        onTabChange={(key) => {
          setActiveTabKey(key as TabKey);
        }}
      >
        {activeTabKey && tabContents[activeTabKey]}
      </Card>
      {meta && visible ? (
        meta.payType === 'stripe' ? (
          <StripeSubscriptionModal
            visible={visible}
            paymentUrl={meta.paymentUrl}
            accountNum={(meta as any).accountNum}
            packageId={meta.packageId as any}
            discountTotalPrice={meta.discountTotalPrice}
            currency={meta.currency as any}
            onRefresh={() => onRefresh(meta)}
            onSubscribed={refreshAfterPay}
            onCancel={() => hide()}
          />
        ) : (
          <PayModal
            visible={visible}
            onCancel={() => hide()}
            afterClose={() => {
              updatePayModal(false, undefined);
            }}
            orderInfo={meta}
            onRefresh={() => onRefresh(meta)}
            onPayed={refreshAfterPay}
          />
        )
      ) : null}
    </Container>
  );
};

export default Market;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
`;
